import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import axiosInstance from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import defaultAdminAvatar from '../../assets/images/default-profile-avatar.svg';
import './Pengaturan.css';

export default function Pengaturan() {
  const { user, logout, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [editingField, setEditingField] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [fieldSaving, setFieldSaving] = useState(false);

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axiosInstance.get('/auth/profile');
        const profileUser = res.data?.user ?? res.data?.data ?? res.data;
        if (profileUser) {
          updateUser({
            name: profileUser.name,
            email: profileUser.email,
            photo_url: profileUser.photo_url,
            photo_version: profileUser.photo_url ? Date.now() : undefined,
          });
          if (profileUser.photo_url) {
            setPhotoPreview(profileUser.photo_url);
          }
        }
      } catch (err) {
        console.error('Gagal memuat profil admin:', err);
      }
    };
    loadProfile();
  }, [updateUser]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const displayPhoto = photoPreview || user?.photo_url || null;
  const displayPhotoSrc = displayPhoto
    ? `${displayPhoto}${displayPhoto.includes('?') ? '&' : '?'}v=${user?.photo_version || 0}`
    : defaultAdminAvatar;

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      showMessage('error', 'Format foto harus JPG, PNG, atau WEBP');
      return;
    }

    setPhotoLoading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const res = await axiosInstance.post('/auth/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const photoUrl = res.data?.photo_url || res.data?.user?.photo_url;

      if (photoUrl) {
        setPhotoPreview(photoUrl);
        updateUser({
          photo_url: photoUrl,
          photo_version: Date.now(),
        });
      }

      showMessage('success', 'Foto profil berhasil diperbarui');
    } catch (err) {
      const data = err.response?.data;
      const photoError = data?.errors?.photo?.[0];
      showMessage('error', photoError || data?.message || 'Gagal mengunggah foto profil');
    } finally {
      setPhotoLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startEditField = (field) => {
    setEditingField(field);
    setDraftValue(field === 'name' ? (user?.name || '') : (user?.email || ''));
  };

  const cancelEditField = () => {
    setEditingField(null);
    setDraftValue('');
  };

  const saveField = async (field) => {
    const value = draftValue.trim();

    if (!value) {
      showMessage('error', field === 'name' ? 'Nama wajib diisi' : 'Email wajib diisi');
      return;
    }

    setFieldSaving(true);
    try {
      const payload = field === 'name' ? { name: value } : { email: value };
      const res = await axiosInstance.put('/auth/profile', payload);
      const updated = res.data?.user ?? res.data;

      updateUser({
        name: updated?.name ?? user?.name,
        email: updated?.email ?? user?.email,
        photo_url: updated?.photo_url ?? user?.photo_url,
      });

      setEditingField(null);
      setDraftValue('');
      showMessage(
        'success',
        field === 'name' ? 'Nama berhasil diperbarui' : 'Email berhasil diperbarui'
      );
    } catch (err) {
      const data = err.response?.data;
      const fieldError = data?.errors?.[field]?.[0];
      showMessage('error', fieldError || data?.message || 'Gagal memperbarui profil');
    } finally {
      setFieldSaving(false);
    }
  };

  const renderEditableField = (field, label, type = 'text') => {
    const isEditing = editingField === field;
    const displayValue = field === 'name' ? user?.name : user?.email;

    return (
      <div className="pg-field-item">
        <label>{label}</label>
        {isEditing ? (
          <div className="pg-field-edit">
            <input
              type={type}
              className="pg-profile-input"
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              autoFocus
            />
            <div className="pg-field-edit-actions">
              <button
                type="button"
                className="pg-field-btn pg-field-btn--save"
                onClick={() => saveField(field)}
                disabled={fieldSaving}
              >
                {fieldSaving ? 'Menyimpan...' : 'Simpan'}
              </button>
              <button
                type="button"
                className="pg-field-btn"
                onClick={cancelEditField}
                disabled={fieldSaving}
              >
                Batal
              </button>
            </div>
          </div>
        ) : (
          <div className="pg-field-row">
            <p>{displayValue || '-'}</p>
            <button
              type="button"
              className="pg-field-edit-btn"
              onClick={() => startEditField(field)}
              disabled={editingField && editingField !== field}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    );
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      return showMessage('error', 'Konfirmasi password tidak cocok');
    }

    setLoading(true);
    try {
      await axiosInstance.post('/auth/change-password', {
        current_password: passwordData.current_password,
        password: passwordData.new_password,
        password_confirmation: passwordData.new_password_confirmation,
      });

      showMessage('success', 'Password berhasil diperbarui');
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
    } catch (err) {
      showMessage('error', 'Gagal mengganti password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('Logout semua perangkat?')) return;
    await axiosInstance.post('/auth/logout');
    logout();
  };

  const navItems = [
    { key: 'profile', label: 'Profil' },
    { key: 'security', label: 'Keamanan' },
    { key: 'danger', label: 'Akun' },
  ];

  return (
    <AdminLayout title="">
      <div className="pg-wrapper">

        <div className="pg-header">
          <h2>Pengaturan Akun</h2>
          <p>Kelola informasi dan keamanan akun Anda</p>
        </div>

        <div className="pg-layout">

          <div className="pg-nav">
            {navItems.map(tab => (
              <button
                key={tab.key}
                className={`pg-nav-btn ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="pg-card">

            {message && (
              <div className={`pg-alert ${message.type}`}>
                {message.text}
              </div>
            )}

            {activeTab === 'profile' && (
              <>
                <h3 className="pg-title">Informasi Akun</h3>

                <div className="pg-photo-section">
                  <img
                    src={displayPhotoSrc}
                    alt="Foto profil admin"
                    className="pg-photo-preview"
                  />
                  <div className="pg-photo-actions">
                    <p className="pg-photo-title">Foto Profil</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="pg-photo-input"
                      onChange={handlePhotoSelect}
                    />
                    <button
                      type="button"
                      className="pg-photo-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={photoLoading}
                    >
                      {photoLoading ? 'Mengunggah...' : 'Ganti Foto Profil'}
                    </button>
                  </div>
                </div>

                <div className="pg-info-grid">
                  {renderEditableField('name', 'Nama', 'text')}
                  {renderEditableField('email', 'Email', 'email')}

                  <div className="pg-field-item">
                    <label>Role</label>
                    <p>{user?.role}</p>
                  </div>

                  <div className="pg-field-item">
                    <label>Terdaftar</label>
                    <p>
                      {user?.created_at
                        ? new Date(user.created_at).toLocaleDateString('id-ID')
                        : '-'}
                    </p>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'security' && (
              <>
                <h3 className="pg-title">Ganti Password</h3>

                <form onSubmit={handlePasswordUpdate} className="pg-form">
                  <input
                    type="password"
                    placeholder="Password lama"
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, current_password: e.target.value })
                    }
                  />

                  <input
                    type="password"
                    placeholder="Password baru"
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, new_password: e.target.value })
                    }
                  />

                  <input
                    type="password"
                    placeholder="Konfirmasi password"
                    value={passwordData.new_password_confirmation}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        new_password_confirmation: e.target.value,
                      })
                    }
                  />

                  <button disabled={loading}>
                    {loading ? 'Menyimpan...' : 'Update Password'}
                  </button>
                </form>
              </>
            )}

            {activeTab === 'danger' && (
              <>
                <h3 className="pg-title text-danger">Manajemen Akun</h3>

                <div className="pg-danger-box">
                  <div>
                    <p className="pg-danger-title">Logout semua perangkat</p>
                    <span>Sesi login akan dihentikan</span>
                  </div>

                  <button className="pg-btn-danger" onClick={handleLogoutAll}>
                    Logout
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
